import { useEffect, useMemo, useState } from 'react';
import { Filter, Loader2, RefreshCw } from 'lucide-react';
import { warehouseService } from '../services/warehouseService';
import { exportRowsToExcel } from '../utils/excelExport';

type WaitingRow = {
  id: string;
  shipmentNo: string;
  shipmentId: string;
  vehiclePlate: string;
  driverName: string;
  location: string;
  locationType: string;
  source: string;
  status: string;
  startLocal: string;
  endLocal: string;
  startLat: number | null;
  startLng: number | null;
  endLat: number | null;
  endLng: number | null;
  startSource: string;
  endSource: string;
  durationMinutes: number;
  waitingReason: string;
  notes: string;
};

const toLocal = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('tr-TR');
};

const mapRow = (item: any): WaitingRow => ({
  id: String(item.id),
  shipmentNo: item.shipment_no || item.order_number || '-',
  shipmentId: item.shipment_id ? String(item.shipment_id) : '',
  vehiclePlate: item.vehicle_plate || '-',
  driverName: item.driver_name || '-',
  location: item.location || '-',
  locationType: item.location_type || item.waiting_type || '-',
  source: item.source || '-',
  status: item.status || '-',
  startLocal: item.waiting_start_local || item.waiting_start || item.reported_at || '',
  endLocal: item.waiting_end_local || item.waiting_end || item.resolved_at || '',
  startLat: item.start_latitude != null ? Number(item.start_latitude) : null,
  startLng: item.start_longitude != null ? Number(item.start_longitude) : null,
  endLat: item.end_latitude != null ? Number(item.end_latitude) : null,
  endLng: item.end_longitude != null ? Number(item.end_longitude) : null,
  startSource: item.start_location_source || '',
  endSource: item.end_location_source || '',
  durationMinutes: Number(item.duration_minutes || 0),
  waitingReason: item.reason || '-',
  notes: item.notes || '-',
});

export function Beklemeler() {
  const [rows, setRows] = useState<WaitingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ shipmentNo: '', vehiclePlate: '', driverName: '', status: '' });

  const load = async () => {
    try {
      setLoading(true);
      const response: any = await warehouseService.getWaitingRecords({
        shipmentNo: filters.shipmentNo || undefined,
        vehiclePlate: filters.vehiclePlate || undefined,
        driverName: filters.driverName || undefined,
        status: filters.status || undefined,
      });
      const data = Array.isArray(response?.data) ? response.data : [];
      setRows(data.map(mapRow));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const exportRows = useMemo(() => rows.map((row) => ({
    SiparisNo: row.shipmentNo,
    Plaka: row.vehiclePlate,
    Surucu: row.driverName,
    Lokasyon: row.location,
    Tip: row.locationType,
    Durum: row.status,
    Baslama: toLocal(row.startLocal),
    Bitis: toLocal(row.endLocal),
    SureDakika: row.durationMinutes,
    BaslangicKonum: row.startLat != null && row.startLng != null ? `${row.startLat}, ${row.startLng}` : '-',
    BitisKonum: row.endLat != null && row.endLng != null ? `${row.endLat}, ${row.endLng}` : '-',
    BaslangicKaynak: row.startSource || '-',
    BitisKaynak: row.endSource || '-',
    KayitKaynagi: row.source,
    Neden: row.waitingReason,
    Not: row.notes,
  })), [rows]);

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Beklemeler</h1>
          <p className="mt-1 text-sm text-gray-500">Sevkiyata `LEFT JOIN` bagli, baslat ve bitir verileri tek satirda.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => void load()} className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <RefreshCw className="h-4 w-4" /> Yenile
          </button>
          <button onClick={() => exportRowsToExcel(exportRows as any[], `beklemeler-${new Date().toISOString().slice(0, 10)}.xlsx`, { sheetName: 'Beklemeler' })} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
            Excel Al
          </button>
        </div>
      </div>

      <div className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-4">
        <label className="space-y-1 text-sm text-slate-600">
          <span className="inline-flex items-center gap-2 font-medium"><Filter className="h-4 w-4" /> Sevkiyat No</span>
          <input value={filters.shipmentNo} onChange={(e) => setFilters((current) => ({ ...current, shipmentNo: e.target.value }))} className="h-11 w-full rounded-xl border border-slate-300 px-3" placeholder="AK0826..." />
        </label>
        <label className="space-y-1 text-sm text-slate-600">
          <span className="font-medium">Plaka</span>
          <input value={filters.vehiclePlate} onChange={(e) => setFilters((current) => ({ ...current, vehiclePlate: e.target.value }))} className="h-11 w-full rounded-xl border border-slate-300 px-3" placeholder="34ABC123" />
        </label>
        <label className="space-y-1 text-sm text-slate-600">
          <span className="font-medium">Surucu</span>
          <input value={filters.driverName} onChange={(e) => setFilters((current) => ({ ...current, driverName: e.target.value }))} className="h-11 w-full rounded-xl border border-slate-300 px-3" placeholder="Ad Soyad" />
        </label>
        <label className="space-y-1 text-sm text-slate-600">
          <span className="font-medium">Durum</span>
          <select value={filters.status} onChange={(e) => setFilters((current) => ({ ...current, status: e.target.value }))} className="h-11 w-full rounded-xl border border-slate-300 px-3">
            <option value="">Tum durumlar</option>
            <option value="waiting">Devam Eden</option>
            <option value="completed">Tamamlandi</option>
            <option value="pending_approval">Onay Bekliyor</option>
          </select>
        </label>
      </div>

      <div className="flex justify-end">
        <button onClick={() => void load()} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800">Filtrele</button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-[1600px] w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-3 py-3 text-left">Sevkiyat No</th>
                <th className="px-3 py-3 text-left">Plaka</th>
                <th className="px-3 py-3 text-left">Surucu</th>
                <th className="px-3 py-3 text-left">Lokasyon</th>
                <th className="px-3 py-3 text-left">Tip</th>
                <th className="px-3 py-3 text-left">Durum</th>
                <th className="px-3 py-3 text-left">Baslama</th>
                <th className="px-3 py-3 text-left">Bitis</th>
                <th className="px-3 py-3 text-left">Sure</th>
                <th className="px-3 py-3 text-left">Baslangic Konum</th>
                <th className="px-3 py-3 text-left">Bitis Konum</th>
                <th className="px-3 py-3 text-left">Baslangic Kaynak</th>
                <th className="px-3 py-3 text-left">Bitis Kaynak</th>
                <th className="px-3 py-3 text-left">Kayit Kaynagi</th>
                <th className="px-3 py-3 text-left">Bekleme Nedeni</th>
                <th className="px-3 py-3 text-left">Not</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={16} className="px-3 py-10 text-center text-slate-500"><span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Yukleniyor...</span></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={16} className="px-3 py-10 text-center text-slate-500">Kayit bulunamadi</td></tr>
              ) : rows.map((row) => (
                <tr key={row.id} className="border-t border-slate-100 align-top">
                  <td className="px-3 py-3 font-semibold text-slate-900">{row.shipmentNo}</td>
                  <td className="px-3 py-3">{row.vehiclePlate}</td>
                  <td className="px-3 py-3">{row.driverName}</td>
                  <td className="px-3 py-3">{row.location}</td>
                  <td className="px-3 py-3">{row.locationType}</td>
                  <td className="px-3 py-3">{row.status}</td>
                  <td className="px-3 py-3 whitespace-nowrap">{toLocal(row.startLocal)}</td>
                  <td className="px-3 py-3 whitespace-nowrap">{toLocal(row.endLocal)}</td>
                  <td className="px-3 py-3">{row.durationMinutes} dk</td>
                  <td className="px-3 py-3 text-xs">{row.startLat != null && row.startLng != null ? `${row.startLat}, ${row.startLng}` : '-'}</td>
                  <td className="px-3 py-3 text-xs">{row.endLat != null && row.endLng != null ? `${row.endLat}, ${row.endLng}` : '-'}</td>
                  <td className="px-3 py-3">{row.startSource || '-'}</td>
                  <td className="px-3 py-3">{row.endSource || '-'}</td>
                  <td className="px-3 py-3">{row.source}</td>
                  <td className="px-3 py-3">{row.waitingReason}</td>
                  <td className="px-3 py-3">{row.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
